import { useAppSelector } from 'src/store';
import { getAppRoutesAvailability } from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import { insightRouteSettings } from 'src/app/ecology-hub/EcologyHub';
import { FeatureType } from 'src/shared/types';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import useFeature from 'src/shared/hooks/useFeature';
import useProject from 'src/shared/hooks/useProject';
import { NestedSidebarMenuSubItem } from './NestedSidebarMenuItem';

const useEcologyHubMenuItem = (): NestedSidebarMenuSubItem | null => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const { currentProjectFilters, currentProjectSelectedHabitatAssayType } = useProjectFilters();
    const { hasFeature } = useFeature();
    const { isHabitatInsightsProject, isStarterProject } = useProject();

    const isEDnaProject = hasFeature({ feature: FeatureType.EDNA });

    const habitatAssays = currentProjectFilters?.habitatAssays;
    const isSingleTimepoint = !!habitatAssays?.find(entry => entry.key === currentProjectSelectedHabitatAssayType?.key)?.singleTimepoint;
    const hasAnyEdnaSource = currentProjectFilters?.habitatAssays?.some(entry => entry.source === 'eDNA');

    if (!appRoutesAvailability.ecologyHub || (!hasAnyEdnaSource && !isHabitatInsightsProject && !isStarterProject)) {
        return null;
    }

    const isActive = pathname.startsWith('/ecology-hub');

    if (isStarterProject) {
        return {
            title: 'Ecology Hub',
            testId: 'ecology-hub-menu-button',
            onClick: navigate.toEcologyHub,
            isActive,
        };
    }

    const subItems = [
        ...(isHabitatInsightsProject
            ? [
                  {
                      title: insightRouteSettings.habitatTrends.name,
                      onClick: () => navigate.toHabitatTrends(),
                      isActive: pathname.includes(insightRouteSettings.habitatTrends.path),
                  },
              ]
            : []),
        ...(isEDnaProject && hasAnyEdnaSource
            ? [
                  ...(!isSingleTimepoint
                      ? [
                            {
                                title: insightRouteSettings.speciesTrends.name,
                                onClick: () => navigate.toSpeciesTrends(),
                                isActive: pathname.includes(insightRouteSettings.speciesTrends.path),
                            },
                        ]
                      : []),
                  {
                      title: insightRouteSettings.sampleComparison.name,
                      onClick: () => navigate.toSampleComparison(),
                      isActive: pathname.includes(insightRouteSettings.sampleComparison.path),
                  },
                  {
                      title: insightRouteSettings.statisticalComparison.name,
                      onClick: () => navigate.toStatisticalComparison(),
                      isActive: pathname.includes(insightRouteSettings.statisticalComparison.path),
                  },
                  {
                      title: insightRouteSettings.communityComparison.name,
                      onClick: () => navigate.toCommunityComparison(),
                      isActive: pathname.includes(insightRouteSettings.communityComparison.path),
                  },
                  {
                      title: insightRouteSettings.taxonomicComposition.name,
                      onClick: () => navigate.toTaxonomicComposition(),
                      isActive: pathname.includes(insightRouteSettings.taxonomicComposition.path),
                  },
              ]
            : []),
    ];

    const namedSubItems = subItems.map(item => ({
        ...item,
        testId: `${item.title.toLowerCase().replace(/\s+/g, '-')}-menu-item`,
    }));

    return {
        title: 'Ecology Hub',
        testId: 'ecology-hub-menu-button',
        subItems: namedSubItems,
    };
};

export default useEcologyHubMenuItem;
